import * as React from 'react'
import { create } from '@bufbuild/protobuf'
import { FieldMaskSchema } from '@bufbuild/protobuf/wkt'
import { Check, ChevronDown, Loader2 } from 'lucide-react'
import { toast } from 'sonner'

import { UpdateInvoiceRequestSchema } from '@/gen/cortado/v1/cortado_pb'
import type { Invoice } from '@/gen/cortado/v1/invoices_pb'
import { InvoiceStatusEnum } from '@/gen/cortado/v1/settings_pb'
import { getErrorDescription } from '@/lib/error-utils'
import { captureError } from '@/lib/sentry'
import { getInvoiceStatusLabel } from '@/lib/enum-utils'
import { cn } from '@/lib/utils'
import { useUpdateInvoice } from '@/stores/invoices.store'
import { usePermissions } from '@/hooks/use-permissions'
import { permissionDeniedMessage } from '@/components/shared/action-gate'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { InvoiceStatusBadge } from '@/components/shared/enum-badge'
import { VoidInvoiceDialog } from './void-invoice-dialog'

interface InvoiceStatusDropdownProps {
  invoice: Invoice
  className?: string
}

const invoiceStatusOptions = [
  InvoiceStatusEnum.INVOICE_STATUS_DRAFT,
  InvoiceStatusEnum.INVOICE_STATUS_SENT,
  InvoiceStatusEnum.INVOICE_STATUS_VIEWED,
  InvoiceStatusEnum.INVOICE_STATUS_PAYMENT_PENDING,
  InvoiceStatusEnum.INVOICE_STATUS_PARTIALLY_PAID,
  InvoiceStatusEnum.INVOICE_STATUS_PAID,
  InvoiceStatusEnum.INVOICE_STATUS_OVERDUE,
  InvoiceStatusEnum.INVOICE_STATUS_CANCELLED,
  InvoiceStatusEnum.INVOICE_STATUS_OVERPAID,
]

interface VoidEligibility {
  hasPayments: boolean
  canManagePayments: boolean
}

function getDisabledStatusReason(
  targetStatus: InvoiceStatusEnum,
  currentStatus: InvoiceStatusEnum,
  { hasPayments, canManagePayments }: VoidEligibility
): string | undefined {
  if (targetStatus === currentStatus) {
    return undefined
  }

  if (currentStatus === InvoiceStatusEnum.INVOICE_STATUS_DRAFT && targetStatus === InvoiceStatusEnum.INVOICE_STATUS_SENT) {
    return undefined
  }

  switch (targetStatus) {
    case InvoiceStatusEnum.INVOICE_STATUS_DRAFT:
      return 'Draft is only set when an invoice is created.'
    case InvoiceStatusEnum.INVOICE_STATUS_SENT:
      return 'Only draft invoices can be manually published.'
    case InvoiceStatusEnum.INVOICE_STATUS_VIEWED:
      return 'Viewed is set by client portal activity.'
    case InvoiceStatusEnum.INVOICE_STATUS_PAYMENT_PENDING:
      return 'Payment pending is set by checkout activity.'
    case InvoiceStatusEnum.INVOICE_STATUS_PARTIALLY_PAID:
    case InvoiceStatusEnum.INVOICE_STATUS_PAID:
    case InvoiceStatusEnum.INVOICE_STATUS_OVERPAID:
      return 'Payment statuses are set by recorded payments.'
    case InvoiceStatusEnum.INVOICE_STATUS_OVERDUE:
      return 'Overdue is set from payment schedule due dates.'
    case InvoiceStatusEnum.INVOICE_STATUS_CANCELLED:
      if (hasPayments) {
        return 'Invoices with recorded payments cannot be voided. Refund or void the payments first.'
      }
      if (!canManagePayments) {
        return permissionDeniedMessage('void', 'invoices')
      }
      return undefined
    default:
      return 'This status cannot be changed manually.'
  }
}

export function InvoiceStatusDropdown({ invoice, className }: InvoiceStatusDropdownProps) {
  const invoiceId = invoice.id
  const currentStatus = invoice.status
  const [open, setOpen] = React.useState(false)
  const [voidDialogOpen, setVoidDialogOpen] = React.useState(false)
  const { updateInvoice, isLoading } = useUpdateInvoice()
  const { can } = usePermissions(invoice.organizationId)
  const voidEligibility: VoidEligibility = {
    hasPayments: Number(invoice.amountPaidCents || 0n) > 0,
    canManagePayments: can('PaymentsManage'),
  }

  const handleStatusChange = async (newStatus: InvoiceStatusEnum) => {
    if (newStatus === currentStatus) {
      setOpen(false)
      return
    }

    // Cancelling also cancels the payment schedules, so it goes through the
    // void flow rather than a bare status update.
    if (newStatus === InvoiceStatusEnum.INVOICE_STATUS_CANCELLED) {
      setOpen(false)
      setVoidDialogOpen(true)
      return
    }

    try {
      await updateInvoice(
        create(UpdateInvoiceRequestSchema, {
          id: invoiceId,
          status: newStatus,
          updateMask: create(FieldMaskSchema, {
            paths: ['status'],
          }),
        })
      )
      toast.success(`Status updated to ${getInvoiceStatusLabel(newStatus)}`)
      setOpen(false)
    } catch (error) {
      captureError(error instanceof Error ? error : new Error(String(error)), {
        context: 'invoice-status-update',
      })
      toast.error('Failed to update invoice status', {
        description: getErrorDescription(error),
      })
    }
  }

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={cn('w-full justify-between gap-2', className)}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Updating...
              </>
            ) : (
              <>
                <span>{getInvoiceStatusLabel(currentStatus)}</span>
                <ChevronDown className="h-4 w-4" />
              </>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-64 p-2">
          <div className="space-y-1">
            <p className="px-2 py-1.5 text-xs font-medium text-muted-foreground">Client visibility</p>
            <p className="px-2 pb-1 text-xs text-muted-foreground">
              Manual changes are limited to publishing drafts and voiding unpaid invoices.
            </p>
            {invoiceStatusOptions.map((status) => {
              const isCurrent = status === currentStatus
              const disabledReason = getDisabledStatusReason(status, currentStatus, voidEligibility)
              return (
                <Button
                  key={status}
                  variant="ghost"
                  size="sm"
                  className={cn('h-auto w-full justify-start px-2 py-2 text-left', isCurrent && 'bg-accent')}
                  onClick={() => handleStatusChange(status)}
                  disabled={isCurrent || Boolean(disabledReason)}
                >
                  <div className="flex w-full items-start gap-2">
                    <InvoiceStatusBadge status={status} className="flex-shrink-0" />
                    {disabledReason && (
                      <p className="min-w-0 flex-1 whitespace-normal text-xs font-normal leading-snug text-muted-foreground">
                        {disabledReason}
                      </p>
                    )}
                    {isCurrent && <Check className="ml-auto h-4 w-4 flex-shrink-0 text-muted-foreground" />}
                  </div>
                </Button>
              )
            })}
          </div>
        </PopoverContent>
      </Popover>
      <VoidInvoiceDialog invoiceId={invoiceId} open={voidDialogOpen} onOpenChange={setVoidDialogOpen} />
    </>
  )
}
